import { css } from '@emotion/react'
import { memo, RefObject, useCallback, useState } from 'react'
import { VListHandle } from 'virtua'
import { AppStateType } from '../../../../constants/enums'
import Emoji, { IEmoji } from '../../../../mfcLogic/emoji'
import { useAppStateSelector } from '../../../Hooks/useAppState'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxWindowWidth,
    smallHeightDialogCheck,
} from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import { greenMFC, PAPER_HEIGHTS } from '../../../theme'
import GridVirtual from '../../GridVirtual'
import InputSearch from '../../InputSearch'
import NoResults from '../../Search/NoResults'
import Emoticon from './Emoticon'

const noResultsStyles = css({
    height: 508,
    padding: getThemeSpacing(2),
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        height: 278,
    },
    '@media screen and (max-height: 412px)': {
        height: 133,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
})

const gridRootStyles = css({
    height: 508,
    width: 444,
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        height: 278,
    },
    '@media screen and (max-height: 412px)': {
        height: 133,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
    [smallHeightDialogCheck]: {
        height: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
    zIndex: PAPER_HEIGHTS.base,
    flex: '0 1 auto',
    padding: '0 2px',
    background: palette.dialogContrastBackground,
})

interface IEmoticon extends IEmoji {
    key: string
}

const buildEmoticons = () => {
    const uniqueEmoticons: Record<number, boolean> = {}
    return Object.keys(Emoji).reduce((acc, key) => {
        const value: IEmoji = Emoji[key]
        const { id, width, height, tags = '' } = value
        if (uniqueEmoticons[id]) return acc
        uniqueEmoticons[id] = true
        return [
            ...acc,
            {
                height,
                id,
                key,
                width,
                tags: tags ? tags.concat(',' + key) : key,
            },
        ]
    }, [] as Array<IEmoticon>)
}

const EMOTICONS = buildEmoticons()

const searchEmoji = (query: string) => {
    const lowercaseQuery = query.trim().toLowerCase()
    const res = EMOTICONS.filter(({ tags }) => tags.includes(lowercaseQuery))
    return res
}

interface IEmojis {
    initialValue: string
    onInsertEmoticon(key: string): void
    onQueryUpdate(query: string): void
    showSearch?: boolean
}

const Emojis = memo((props: IEmojis) => {
    const {
        initialValue,
        onInsertEmoticon,
        onQueryUpdate,
        showSearch = true,
    } = props
    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const [emoji, setEmoji] = useState<Array<IEmoji>>(EMOTICONS)
    const showNoResults = emoji.length === 0

    const onUpdateList = useCallback((ref: RefObject<VListHandle>) => {
        ref.current?.scrollTo(0)
    }, [])

    const results = showNoResults ? (
        <NoResults
            styleOverrides={noResultsStyles}
            searchQuery={initialValue}
        />
    ) : (
        <GridVirtual
            styleOverrides={gridRootStyles}
            columns={5}
            rowHeight={64}
            list={emoji}
            onUpdateList={onUpdateList}
            testId="EmojiGif__Emojis"
        >
            <Emoticon onClickEmoticon={onInsertEmoticon} darkMode={darkMode} />
        </GridVirtual>
    )

    const handleSearchEmoji = useCallback(
        (query: string) => {
            onQueryUpdate(query)
            const nextResults = searchEmoji(query)
            setEmoji(nextResults)
        },
        [onQueryUpdate],
    )

    return (
        <>
            {showSearch && (
                <InputSearch
                    defaultValue={initialValue}
                    onChange={handleSearchEmoji}
                    placeholder="Search Emoji"
                    clearInputIcon="closeCircle"
                    clearInputIconColor={greenMFC}
                    quietFocus
                />
            )}
            {results}
        </>
    )
})

export default Emojis
